import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { format, addDays, startOfDay, isBefore } from "date-fns";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CALENDAR_ID = "BTLjBxPvHmiOEw9GPAeA";
const LOCATION_ID = "KBMLg03QdkeXf4f29Q41";
const VIBE_API_URL = "https://backend.leadconnectorhq.com/vibe-ai";

export function BookingWidget() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [slots, setSlots] = useState<string[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<"date" | "form" | "success">("date");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    notes: "",
  });

  useEffect(() => {
    if (!date) {
      setSlots([]);
      return;
    }

    const fetchSlots = async () => {
      setIsLoadingSlots(true);
      try {
        const startDate = startOfDay(date).getTime();
        const endDate = addDays(startOfDay(date), 1).getTime();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const res = await fetch(
          `https://backend.leadconnectorhq.com/calendars/${CALENDAR_ID}/free-slots?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}`
        );
        const data = await res.json();
        
        const dateString = format(date, "yyyy-MM-dd");
        const availableSlots = data[dateString]?.slots || [];
        setSlots(availableSlots);
      } catch (error) {
        console.error("Failed to fetch slots", error);
        toast({ title: "Error", description: "Failed to load available times.", variant: "destructive" });
      } finally {
        setIsLoadingSlots(false);
      }
    };

    fetchSlots();
    setSelectedSlot(null);
  }, [date, toast]);

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${VIBE_API_URL}/booking/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          locationId: LOCATION_ID,
          calendarId: CALENDAR_ID,
          ...formData,
          selectedSlot,
          selectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          sessionId: crypto.randomUUID(),
          customFields: [],
        }),
      });

      if (!response.ok) throw new Error("Booking failed");

      navigate("/thank-you");
    } catch (error) {
      console.error("Booking error", error);
      toast({ title: "Error", description: "Failed to book appointment. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full text-left">
      {step === "date" ? (
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4">1. Select a Date</h3>
            <div className="bg-background/50 border border-border/50 rounded-2xl p-2 sm:p-6 flex justify-center shadow-inner overflow-x-auto w-full max-w-full custom-scrollbar">
              <div className="w-full flex justify-center min-w-[260px]">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => isBefore(d, startOfDay(new Date())) || isBefore(addDays(new Date(), 31), d)}
                  className="rounded-md p-1 sm:p-3"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <h3 className="text-xl font-bold mb-4">2. Select a Time</h3>
            <div className="bg-background/50 border border-border/50 rounded-2xl p-4 sm:p-6 flex-grow max-h-[350px] overflow-y-auto shadow-inner">
              {!date ? (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  Please select a date first
                </div>
              ) : isLoadingSlots ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : slots.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  No available times for this date
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {slots.map((slot) => (
                    <Button
                      key={slot}
                      variant={selectedSlot === slot ? "default" : "outline"}
                      className={`w-full h-12 transition-all ${selectedSlot === slot ? "bg-primary text-primary-foreground border-primary shadow-[0_0_15px_hsl(var(--primary)/0.4)]" : "border-border/50 hover:border-primary/50 bg-card/50"}`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      {format(new Date(slot), "h:mm a")}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mt-6">
              <Button 
                disabled={!selectedSlot} 
                onClick={() => setStep("form")}
                className="w-full h-14 text-lg relative group overflow-hidden bg-primary text-primary-foreground font-bold rounded-full shadow-[0_0_20px_hsl(var(--primary)/0.3)] hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
                <span className="relative z-10">Continue</span>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="animate-in slide-in-from-right-4 fade-in duration-300">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => setStep("date")} className="rounded-full hover:bg-white/5">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h3 className="text-2xl font-bold">3. Your Details</h3>
          </div>
          
          <div className="bg-primary/10 border border-primary/20 rounded-2xl p-5 mb-8 flex items-center gap-5 backdrop-blur-sm">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium mb-1">Selected Time</p>
              <p className="font-bold text-lg">{selectedSlot ? format(new Date(selectedSlot), "EEEE, MMMM d, yyyy 'at' h:mm a") : ""}</p>
            </div>
          </div>

          <form onSubmit={handleBooking} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name *</label>
                <Input 
                  required 
                  value={formData.firstName} 
                  onChange={e => setFormData({...formData, firstName: e.target.value})} 
                  className="bg-muted/50 h-12 border-border/50 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name *</label>
                <Input 
                  required 
                  value={formData.lastName} 
                  onChange={e => setFormData({...formData, lastName: e.target.value})} 
                  className="bg-muted/50 h-12 border-border/50 focus-visible:ring-primary"
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email *</label>
                <Input 
                  type="email" 
                  required 
                  value={formData.email} 
                  onChange={e => setFormData({...formData, email: e.target.value})} 
                  className="bg-muted/50 h-12 border-border/50 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone Number *</label>
                <Input 
                  type="tel" 
                  required 
                  value={formData.phone} 
                  onChange={e => setFormData({...formData, phone: e.target.value})} 
                  className="bg-muted/50 h-12 border-border/50 focus-visible:ring-primary"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Company</label>
              <Input 
                value={formData.companyName} 
                onChange={e => setFormData({...formData, companyName: e.target.value})} 
                className="bg-muted/50 h-12 border-border/50 focus-visible:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">How can we help you? *</label>
              <Textarea 
                required 
                value={formData.notes} 
                onChange={e => setFormData({...formData, notes: e.target.value})} 
                className="bg-muted/50 min-h-[120px] border-border/50 focus-visible:ring-primary resize-none"
                placeholder="Tell us a bit about your goals..."
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full h-14 text-lg font-bold rounded-full relative group overflow-hidden bg-primary text-primary-foreground shadow-[0_0_20px_hsl(var(--primary)/0.3)] hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] transition-all duration-300 mt-4"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
              <span className="relative z-10 flex items-center justify-center gap-2">
                {isSubmitting && <Loader2 className="w-5 h-5 animate-spin" />}
                {isSubmitting ? "Confirming..." : "Confirm Booking"}
              </span>
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
